<template>
  <div class="assessments-view">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h4 font-weight-bold">Assessments</h1>
      <div class="d-flex align-center">
        <v-btn 
          variant="outlined"
          color="black" 
          prepend-icon="mdi-plus"
          @click="navigateToCreateAssessment"
          class="create-btn"
        >
          Create New Test
        </v-btn>
      </div>
    </div>

    <div class="assessment-tabs">
      <v-tabs v-model="activeTab" class="mb-4" color="#e94057">
        <v-tab value="all">ALL TEST</v-tab>
        <v-tab value="drafts">DRAFT</v-tab>
        <v-tab value="ongoing">ONGOING</v-tab>
        <v-tab value="completed">COMPLETE</v-tab>
      </v-tabs>
    </div>
    
    <div class="assessment-search mb-4">
      <v-text-field
        v-model="searchTerm"
        prepend-inner-icon="mdi-magnify"
        placeholder="Search Assessments"
        single-line
        hide-details
        variant="outlined"
        density="comfortable"
        @input="onSearchChange"
        bg-color="white"
        class="search-field"
      ></v-text-field>
    </div>
    
    <div v-if="loading" class="assessments-table">
      <v-card class="assessments-table-card mb-4 elevation-0">
        <div class="skeleton-table">
          <!-- Skeleton Table Header -->
          <div class="skeleton-header">
            <div v-for="header in tableHeaders" :key="header.value" class="skeleton-header-cell">
              <div class="skeleton-box skeleton-text" style="width: 60%;"></div>
            </div>
          </div>
          
          <!-- Skeleton Table Rows -->
          <div v-for="i in 5" :key="i" class="skeleton-row">
            <div class="skeleton-cell">
              <div class="skeleton-box skeleton-text" style="width: 80%;"></div>
            </div>
            <div class="skeleton-cell">
              <div class="skeleton-box skeleton-text" style="width: 60%;"></div>
            </div>
            <div class="skeleton-cell">
              <div class="skeleton-box skeleton-text" style="width: 70%;"></div>
            </div>
            <div class="skeleton-cell">
              <div class="skeleton-box skeleton-text" style="width: 70%;"></div>
            </div>
            <div class="skeleton-cell">
              <div class="skeleton-progress-wrapper">
                <div class="skeleton-box skeleton-progress" style="width: 80px;"></div>
                <div class="skeleton-box skeleton-text" style="width: 50px; margin-left: 8px;"></div>
              </div>
            </div>
            <div class="skeleton-cell" style="text-align: right;">
              <div class="skeleton-box skeleton-chip" style="width: 80px; margin-left: auto;"></div>
            </div>
            <div class="skeleton-cell" style="text-align: right;">
              <div class="skeleton-actions">
                <div class="skeleton-box skeleton-button"></div>
                <div class="skeleton-box skeleton-button"></div>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </div>
    
    <div v-else-if="error" class="assessments-error">
      <p>{{ error }}</p>
      <v-btn @click="fetchAssessments" color="primary">Try Again</v-btn>
    </div>
    
    <div v-else-if="displayedAssessments.length === 0" class="assessments-empty">
      <p>No Assessments found.</p>
      <ActionButton variant="primary" @click="navigateToCreateAssessment">Create Your First Assessment</ActionButton>
    </div>
    
    <div v-else class="assessments-table">
      <v-card class="assessments-table-card mb-4 elevation-0">
        <DataTable
          :headers="tableHeaders"
          :items="displayedAssessments"
          :loading="loading"
          :current-page="1"
          :total-pages="1"
          :items-per-page="-1"
          :show-pagination="false"
          class="assessments-data-table"
        >
          <!-- Name column -->
          <template #column-title="{ item }">
            <div class="font-weight-medium">{{ item.title }}</div>
          </template>
          
          <!-- Brand column -->
          <template #column-visual_identity_system_name="{ item }">
            <div>{{ item.visual_identity_system_name }}</div>
          </template>
          
          <!-- Date column -->
          <template #column-created_at="{ item }">
            <div>{{ formatDate(item.created_at) }}</div>
          </template>
          
          <!-- Deadline column -->
          <template #column-deadline="{ item }">
            <div>{{ formatDate(item.deadline) }}</div>
          </template>
          
          <!-- First Status column (completion) -->
          <template #column-completionStatus="{ item }">
            <!-- Show responses count for drafts -->
            <div v-if="activeTab === 'drafts'">
              <div class="status-label">{{ item.response_count || 0 }} Responses</div>
            </div>
            <!-- Custom progress bar for all other tabs -->
            <div v-else class="d-flex align-center">
              <div class="custom-progress-container mr-2">
                <div class="custom-progress-track">
                  <div
                    class="custom-progress-fill"
                    :style="{ width: Math.round(getCompletionPercentage(item)) + '%' }"
                  ></div>
                </div>
              </div>
              <span class="text-caption font-weight-medium">
                {{ Math.round(getCompletionPercentage(item)) }}%
                ({{ getCompletedCount(item) }}/{{ getTotalExperts(item) }})
              </span>
            </div>
          </template>
          
          <!-- Second Status column (state) -->
          <template #column-status="{ item }">
            <div class="d-flex justify-end">
              <v-chip
                :color="getStatusColor(item)"
                :text-color="getStatusTextColor()"
                class="status-chip"
                size="small"
              >
                {{ getStatusText(item) }}
              </v-chip>
            </div>
          </template>
          
          <!-- Actions column -->
          <template #column-actions="{ item }">
            <div class="d-flex justify-end gap-4">
              <v-menu open-on-hover offset-y>
                <template #activator="{ props, on }">
                  <ActionButton v-bind="props" v-on="on" variant="secondary">Actions</ActionButton>
                </template>
                <v-list>
                  <v-list-item @click="navigateToResponses(item.id)">
                    <v-list-item-title>View Responses</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="navigateToAssessmentDetail(item.id)">
                    <v-list-item-title>View Assessment</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="navigateToPreviewAssessment(item.id)">
                    <v-list-item-title>Preview Assessment</v-list-item-title>
                  </v-list-item>
                  <!-- Show Send Invitations in dropdown when all invitations are sent -->
                  <v-list-item 
                    v-if="getPendingInvitationsCount(item) === 0 && hasInvitedExperts(item)" 
                    @click="openInvitationDialog(item)"
                  >
                    <v-list-item-title>Send Invitations</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="isAdmin" @click="editAssessment(item)">
                    <v-list-item-title>Edit Assessment</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="isAdmin" @click="duplicateAssessment(item)">
                    <v-list-item-title>Duplicate Assessment</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="isAdmin" @click="confirmDeleteAssessment(item)">
                    <v-list-item-title>Delete Assessment</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              
              <!-- Dynamic primary button based on invitation status -->
              <ActionButton 
                v-if="isEdgeCaseCompletedWithoutExperts(item)"
                variant="primary" 
                @click="navigateToResponses(item.id)"
              >
                View Responses
              </ActionButton>

              <ActionButton 
                v-else-if="getPendingInvitationsCount(item) > 0" 
                variant="primary" 
                @click="openInvitationDialog(item)"
              >
                {{ getInvitationButtonText(item) }}
              </ActionButton>
              
              <!-- Show View Responses as primary when all invitations sent -->
              <ActionButton 
                v-else-if="getPendingInvitationsCount(item) === 0 && hasInvitedExperts(item)" 
                variant="primary" 
                @click="navigateToResponses(item.id)"
              >
                View Responses
              </ActionButton>
              
              <!-- Show Send Invitations as primary when no invitations have been sent yet -->
              <ActionButton 
                v-else 
                variant="primary" 
                @click="openInvitationDialog(item)"
              >
                Send Invitations
              </ActionButton>
            </div>
          </template>
        </DataTable>
      </v-card>
    </div>
    
    <!-- Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5">Delete Assessment</v-card-title>
        <v-card-text>
          Are you sure you want to delete this assessment? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey-darken-1" variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="red" variant="text" @click="deleteAssessment">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Invitation Dialog -->
    <v-dialog v-model="showInvitationDialog" max-width="600">
      <v-card>
        <v-card-title>Email Invitations</v-card-title>
        <v-card-text>
          <div v-if="selectedAssessment" class="mb-4">
            <v-alert 
              v-if="getPendingInvitationsCount(selectedAssessment) > 0" 
              type="info" 
              variant="tonal" 
              class="mb-4"
            >
              <strong>{{ getPendingInvitationsCount(selectedAssessment) }}</strong> 
              expert{{ getPendingInvitationsCount(selectedAssessment) === 1 ? '' : 's' }} 
              {{ getPendingInvitationsCount(selectedAssessment) === 1 ? 'has' : 'have' }} not been invited yet.
            </v-alert>
            
            <v-alert 
              v-else-if="selectedAssessment.invitation_stats && selectedAssessment.invitation_stats.invited_count > 0" 
              type="warning" 
              variant="tonal" 
              class="mb-4"
            >
              All experts have already been invited. You can resend invitations if needed.
            </v-alert>
          </div>
          
          <v-text-field label="Subject" v-model="invitationSubject" dense outlined />
          <v-textarea label="Body" v-model="invitationBody" rows="4" dense outlined />
          <v-file-input label="Attachment (optional)" v-model="invitationAttachment" dense outlined />
          
          <div class="text-caption mt-2">
            <strong>Available placeholders:</strong><br>
            • [expert_name] - Expert's full name<br>
            • [assessment_title] - Assessment title
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeInvitationDialog">Cancel</v-btn>
          <v-btn color="#e94057" @click="sendInvitations" :loading="invitationLoading">
            {{ selectedAssessment && getPendingInvitationsCount(selectedAssessment) > 0 ? 'Send' : 'Resend' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import DataTable from '@/components/DataTable.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import apiService from '@/services/apiService';
import { useToast } from 'vue-toastification';

export default {
  name: 'AssessmentsView',
  components: {
    DataTable,
    ActionButton
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      activeTab: 'all',
      searchTerm: '',
      searchDebounce: null,
      
      // Table configuration
      tableHeaders: [
        { text: 'Name', value: 'title', align: 'start', sortable: true },
        { text: 'Brand', value: 'visual_identity_system_name', align: 'start', sortable: true },
        { text: 'Date', value: 'created_at', align: 'start', sortable: true },
        { text: 'Deadline', value: 'deadline', align: 'start', sortable: true },
        { text: 'Status', value: 'completionStatus', align: 'start', sortable: false },
        { text: 'Status', value: 'status', align: 'end', sortable: false },
        { text: '', value: 'actions', align: 'end', sortable: false },
      ],
      
      // Delete confirmation
      showDeleteDialog: false,
      assessmentToDelete: null,
      
      // Invitation modal
      showInvitationDialog: false,
      invitationAssessmentId: null,
      invitationSubject: '',
      invitationBody: '',
      invitationAttachment: null,
      invitationLoading: false,
      selectedAssessment: null
    };
  },
  computed: {
    ...mapState({
      loading: state => state.assessments.loading,
      error: state => state.assessments.error,
      pagination: state => state.assessments.pagination,
      user: state => state.user
    }),
    ...mapGetters({
      allAssessments: 'assessments/allAssessments',
      newAssessments: 'assessments/newAssessments',
      ongoingAssessments: 'assessments/ongoingAssessments',
      completedAssessments: 'assessments/completedAssessments',
      draftAssessments: 'assessments/draftAssessments',
      filteredAssessments: 'assessments/filteredAssessments'
    }),
    
    // Add isAdmin computed property
    isAdmin() {
      return this.user && (this.user.is_staff || this.user.is_superuser);
    },
    
    displayedAssessments() {
      // Always rely on the store-level filteredAssessments getter which combines
      // the current status and searchTerm filters.
      return this.filteredAssessments;
    },
    
    currentPage() {
      return this.pagination.currentPage;
    },
    
    totalPages() {
      return this.pagination.totalPages;
    },
    
    pageSize() {
      return this.pagination.pageSize;
    }
  },
  methods: {
    ...mapActions({
      fetchAssessmentsList: 'assessments/fetchAssessments',
      deleteAssessmentAction: 'assessments/deleteAssessment',
      updateAssessmentAction: 'assessments/updateAssessment'
    }),
    
    fetchAssessments() {
      this.fetchAssessmentsList({});
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()} ${date.getFullYear()}`;
    },
    
    generateCaseCode(id) {
      // Pad the ID to 8 digits
      return id ? id.toString().padStart(8, '0') : '00000000';
    },
    
    isDeadlinePassed(assessment) {
      if (!assessment.deadline) return false;
      const deadline = new Date(assessment.deadline);
      if (isNaN(deadline.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      deadline.setHours(23, 59, 59, 999);
      return today > deadline;
    },

    getStatusText(assessment) {
      if (assessment.is_draft) {
        return 'Draft';
      }
      if (assessment.completion_percentage === 100) {
        return 'Completed';
      }
      if (this.isDeadlinePassed(assessment)) {
        return 'Finished';
      }
      return 'Ongoing';
    },
    
    getStatusColor(assessment) {
      if (assessment.is_draft) {
        return 'blue-grey-lighten-3';
      }
      if (assessment.completion_percentage === 100) {
        return 'green';
      }
      if (this.isDeadlinePassed(assessment)) {
        return '#9e9e9e';
      }
      return 'amber';
    },
    
    getStatusTextColor() {
      return this.activeTab === 'drafts' ? 'black' : 'white';
    },
    
    // Completion helpers (match ResponsesView formatting)
    getCompletionPercentage(assessment) {
      if (assessment && typeof assessment.completion_percentage === 'number') {
        return assessment.completion_percentage;
      }
      const total = this.getTotalExperts(assessment);
      const completed = this.getCompletedCount(assessment);
      if (total > 0 && typeof completed === 'number') {
        return (completed / total) * 100;
      }
      return 0;
    },
    isEdgeCaseCompletedWithoutExperts(assessment) {
      const pct = assessment && typeof assessment.completion_percentage === 'number' ? assessment.completion_percentage : 0;
      const expertCount = assessment && typeof assessment.expert_count === 'number' ? assessment.expert_count : 0;
      const responsesCount = assessment && typeof assessment.responses_count === 'number' ? assessment.responses_count : 0;
      return pct === 100 && expertCount === 0 && responsesCount > 0;
    },
    getTotalExperts(assessment) {
      if (this.isEdgeCaseCompletedWithoutExperts(assessment)) {
        return assessment.responses_count || 0;
      }
      if (assessment && typeof assessment.expert_count === 'number') return assessment.expert_count;
      if (
        assessment && assessment.invitation_stats &&
        typeof assessment.invitation_stats.total_experts === 'number'
      ) {
        return assessment.invitation_stats.total_experts;
      }
      return 0;
    },
    getCompletedCount(assessment) {
      if (this.isEdgeCaseCompletedWithoutExperts(assessment)) {
        return assessment.responses_count || 0;
      }
      if (
        assessment && assessment.invitation_stats &&
        typeof assessment.invitation_stats.completed_responses === 'number'
      ) {
        return assessment.invitation_stats.completed_responses;
      }
      const total = this.getTotalExperts(assessment);
      if (assessment && typeof assessment.completion_percentage === 'number' && total > 0) {
        return Math.round((assessment.completion_percentage / 100) * total);
      }
      return 0;
    },
    
    navigateToAssessmentDetail(id) {
      this.$router.push({ name: 'assessmentDetail', params: { id } });
    },
    
    navigateToCreateAssessment() {
      this.$router.push({ name: 'createAssessment' });
    },
    
    editAssessment(assessment) {
      this.$router.push({ name: 'editAssessment', params: { id: assessment.id } });
    },
    
    duplicateAssessment(assessment) {
      try {
        this.toast.info('Duplicating assessment...');
        
        apiService.post(`/assessments/${assessment.id}/duplicate/`)
          .then(response => {
            if (response && response.data) {
              const newAssessment = response.data.new_assessment;
              const message = response.data.detail || 'Assessment duplicated successfully';
              
              this.toast.success(message);
              
              // Refresh the assessments list to show the new duplicate
              this.fetchAssessments();
              
              // Optionally navigate to edit the new assessment
              if (newAssessment && newAssessment.id) {
                this.$router.push({ name: 'editAssessment', params: { id: newAssessment.id } });
              }
            } else {
              this.toast.success('Assessment duplicated successfully');
              this.fetchAssessments();
            }
          })
          .catch(error => {
            console.error('Error duplicating assessment:', error);
            
            let errorMessage = 'Failed to duplicate assessment';
            if (error && error.response && error.response.data && error.response.data.detail) {
              errorMessage = error.response.data.detail;
            }
            
            this.toast.error(errorMessage);
          });
        
      } catch (error) {
        console.error('Error duplicating assessment:', error);
        this.toast.error('Failed to duplicate assessment');
      }
    },
    
    confirmDeleteAssessment(assessment) {
      this.assessmentToDelete = assessment;
      this.showDeleteDialog = true;
    },
    
    async deleteAssessment() {
      if (!this.assessmentToDelete) return;
      
      try {
        await this.deleteAssessmentAction(this.assessmentToDelete.id);
        this.toast.success('Assessment deleted successfully');
      } catch (error) {
        this.toast.error('Failed to delete assessment');
      } finally {
        this.showDeleteDialog = false;
        this.assessmentToDelete = null;
      }
    },
    
    async toggleAssessmentStatus(assessment) {
      try {
        const updatedAssessment = { ...assessment, is_active: !assessment.is_active };
        await this.updateAssessmentAction({ 
          id: assessment.id, 
          data: updatedAssessment 
        });
        
        this.toast.success(`Assessment ${assessment.is_active ? 'deactivated' : 'activated'} successfully`);
      } catch (error) {
        this.toast.error('Failed to update assessment status');
      }
    },
    
    onPageChange(page) {
      // Pagination disabled: no-op
    },
    
    onSearchChange() {
       // Debounce search to avoid too many API calls
       clearTimeout(this.searchDebounce);
       this.searchDebounce = setTimeout(() => {
        this.$store.commit('assessments/SET_FILTER', { key: 'searchTerm', value: this.searchTerm });
        this.fetchAssessments();
       }, 300);
    },
    
    navigateToResponses(id) {
      this.$router.push({ name: 'responseDetail', params: { id } });
    },
    
    navigateToPreviewAssessment(id) {
      this.$router.push({ name: 'previewAssessment', params: { id } });
    },
    
    openInvitationDialog(assessment) {
      this.selectedAssessment = assessment;
      this.invitationAssessmentId = assessment.id;
      
      // Use existing content if available, otherwise use prepopulated templates
      this.invitationSubject = assessment.invitation_subject || 'Invitation to Participate in Design Assessment';
      
      this.invitationBody = assessment.invitation_body || `Dear [expert_name],

We would like to invite you to participate in our design assessment study for [assessment_title].

Your expertise and insights are valuable to us, and we believe your feedback will contribute significantly to improving this design evaluation.

The assessment should take approximately 15-20 minutes to complete.

Please click the link below to begin the assessment:

Thank you for your time and participation.

Best regards`;
      
      this.invitationAttachment = null;
      this.showInvitationDialog = true;
    },
    
    closeInvitationDialog() {
      this.showInvitationDialog = false;
      this.selectedAssessment = null;
      this.invitationAssessmentId = null;
      this.invitationSubject = '';
      this.invitationBody = '';
      this.invitationAttachment = null;
    },
    
    async sendInvitations() {
      if (!this.invitationSubject || !this.invitationBody) {
        this.toast.error('Subject and body are required');
        return;
      }
      
      this.invitationLoading = true;
      
      try {
        const formData = new FormData();
        formData.append('subject', this.invitationSubject);
        formData.append('body', this.invitationBody);
        if (this.invitationAttachment) {
          formData.append('attachment', this.invitationAttachment);
        }
        
        const response = await apiService.post(
          `/assessments/${this.invitationAssessmentId}/send-invitations/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        // Handle the response
        if (response && response.data) {
          const data = response.data;
          const message = data.detail || 'Invitations sent successfully';
          const failedCount = data.failed_count || 0;
          
          if (failedCount > 0) {
            this.toast.warning(message);
          } else {
            this.toast.success(message);
          }
        } else {
          this.toast.success('Invitations sent successfully');
        }
        
        // Refresh the assessments list to update the invitation counts
        await this.fetchAssessments();
        this.closeInvitationDialog();
        
      } catch (error) {
        console.error('Error sending invitations:', error);
        
        let errorMessage = 'Failed to send invitations';
        let isWarning = false;
        
        // Safe error message extraction
        try {
          if (error && typeof error === 'object') {
            if (error.response && typeof error.response === 'object') {
              const status = error.response.status;
              
              if (error.response.data && typeof error.response.data === 'object') {
                if (error.response.data.detail && typeof error.response.data.detail === 'string') {
                  errorMessage = error.response.data.detail;
                  
                  // Handle 400 status codes as informational messages rather than errors
                  if (status === 400) {
                    isWarning = true;
                  }
                }
              }
            } else if (error.message && typeof error.message === 'string') {
              errorMessage = error.message;
            }
          }
        } catch (parseError) {
          console.error('Error parsing error message:', parseError);
        }
        
        // Show appropriate toast based on error type
        if (isWarning) {
          this.toast.warning(errorMessage);
          // Still refresh and close dialog for business logic "errors"
          await this.fetchAssessments();
          this.closeInvitationDialog();
        } else {
          this.toast.error(errorMessage);
        }
      } finally {
        this.invitationLoading = false;
      }
    },
    
    getPendingInvitationsCount(assessment) {
      return assessment.pending_invitations_count || 0;
    },
    
    getInvitationButtonText(assessment) {
      const pendingCount = this.getPendingInvitationsCount(assessment);
      if (pendingCount === 0) {
        return 'Send Invitations';
      } else if (pendingCount === 1) {
        return 'Send 1 invitation';
      } else {
        return `Send ${pendingCount} invitations`;
      }
    },
    
    hasResponses(assessment) {
      // Check if assessment has any responses based on invitation stats
      const stats = assessment.invitation_stats;
      if (stats) {
        return stats.started_responses > 0 || stats.completed_responses > 0;
      }
      // Fallback to completion percentage
      return assessment.completion_percentage > 0;
    },
    
    hasInvitedExperts(assessment) {
      // Check if any experts have been invited based on invitation stats
      const stats = assessment.invitation_stats;
      if (stats) {
        return stats.invited_count > 0;
      }
      // Fallback: if we have invitation subject/body saved, invitations were likely sent
      return assessment.invitation_subject || assessment.invitation_body;
    }
  },
  watch: {
    activeTab(newTab) {
      // Update the status filter (pagination removed)
      this.$store.commit('assessments/SET_FILTER', { key: 'status', value: newTab !== 'all' ? newTab : null });
      this.fetchAssessments();
    }
  },
  created() {
    this.fetchAssessments();
  }
};
</script>

<style scoped>
.assessments-view {
  padding: 24px;
  background-color: #f8f9fa;
}

.create-btn {
  height: 40px;
  background-color: transparent !important;
  color: black;
  font-weight: bold;
  border-radius: 8px;
  border: 1px solid black;
}

.assessment-tabs {
  margin-bottom: 20px;
}

.assessment-search {
  margin-bottom: 20px;
}

.search-field {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.assessments-error, .assessments-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.assessments-error p, .assessments-empty p {
  margin: 20px 0;
  color: #666;
}

.status-wrapper {
  width: 100%;
}

.status-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 4px;
}

.status-chip {
  min-width: 80px;
  justify-content: center;
}

.assessments-table-card {
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
}

.assessments-data-table {
  width: 100%;
  padding: 0 16px;
}

/* Modify table style to add gaps between rows */
.assessments-data-table :deep(table) {
  background-color: #f8f9fa;
  border-collapse: separate;
  border-spacing: 0 8px;
}

.assessments-data-table :deep(tr) {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s;
}

.assessments-data-table :deep(tr:hover) {
  background-color: #f8f9fa;
}

.assessments-data-table :deep(th) {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.assessments-data-table :deep(td) {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

/* Custom Progress Bar Styles */
.custom-progress-container {
  min-width: 80px;
  width: 80px;
}

.custom-progress-track {
  width: 100%;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.custom-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #e94057 0%, #f16575 100%);
  border-radius: 4px;
  transition: width 0.3s ease-in-out;
  min-width: 0;
  animation: progressFill 0.3s ease-out;
}

@keyframes progressFill {
  from {
    transform: scaleX(0);
    transform-origin: left;
  }
  to {
    transform: scaleX(1);
    transform-origin: left;
  }
}

/* Skeleton Loader Styles */
.skeleton-table {
  padding: 16px;
  background-color: #f8f9fa;
}

.skeleton-header {
  display: flex;
  gap: 16px;
  padding: 16px;
  background-color: white;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-header-cell {
  flex: 1;
}

.skeleton-row {
  display: flex;
  gap: 16px;
  padding: 16px;
  background-color: white;
  border-radius: 4px;
  margin-bottom: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.skeleton-cell {
  flex: 1;
  display: flex;
  align-items: center;
}

.skeleton-progress-wrapper {
  display: flex;
  align-items: center;
}

.skeleton-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.skeleton-box {
  background: linear-gradient(
    90deg,
    #e0e0e0 0%,
    #f0f0f0 20%,
    #f0f0f0 40%,
    #e0e0e0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-text {
  height: 16px;
}

.skeleton-progress {
  height: 8px;
  border-radius: 4px;
}

.skeleton-chip {
  height: 24px;
  border-radius: 12px;
}

.skeleton-button {
  height: 36px;
  width: 90px;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style> 